// CalendarOptimized.js - Server-side aggregated calendar
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  ButtonGroup,
  Select,
  MenuItem,
  FormControl,
  Collapse,
  Tooltip,
  useMediaQuery,
  useTheme,
  Chip,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventIcon from '@mui/icons-material/Event';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from './WebSocketContext';
import TooltipContent from './Calendar/TooltipContent';

// Theme colors to match Sidebar
const primaryColor = '#5757b0';
const hoverColor = '#7777d0';
const lightPurple = '#f0f0ff';

// Common styles
const commonStyles = {
  button: {
    py: { xs: 1, sm: 1.5 },
    px: { xs: 1.5, sm: 2.5 },
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    borderRadius: 8,
    boxShadow: '0 2px 5px rgba(87, 87, 176, 0.15)',
    fontWeight: 'medium',
    '&:hover': {
      backgroundColor: hoverColor,
      color: 'white',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(87, 87, 176, 0.2)',
      transition: 'all 0.2s ease-in-out'
    },
    transition: 'all 0.2s ease'
  },
  eventBox: {
    width: { xs: 0.1, sm: 4 },
    height: { xs: 12, sm: 5 },
    borderRadius: { xs: 1.5, sm: 2 },
    cursor: 'pointer',
    m: { xs: '0.5px', sm: '1px' },
    border: '0.5px solid',
    boxShadow: '0px 1px 1px rgba(0,0,0,0.15)',
    '&:hover': {
      transform: 'scaleX(1.5)',
      transition: 'transform 0.2s',
      boxShadow: '0px 2px 6px rgba(0,0,0,0.25)',
      zIndex: 10
    },
    transition: 'all 0.2s ease'
  }
};

// Helper functions for color generation (kept from original)
const generatePastelColor = (type) => {
  const hash = Array.from(type || 'default').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 85%)`;
};

const generateSaturatedColor = (type) => {
  const hash = Array.from(type || 'default').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

const CalendarOptimized = ({
  caseId,
  navigateTo,
  maxEventsPerDay = 10,
  initialExpanded = true,
  onExpandedChange,
  canToggleExpanded = true,
  variant = 'standalone'
}) => {
  const { t } = useTranslation();
  const { sendMessage, subscribeToMessages, isConnected, isValidatingSession } = useWebSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isWidgetVariant = variant === 'widget';
  const shouldStackHeader = isWidgetVariant && isMobile;

  // State for calendar data from server
  const [calendarData, setCalendarData] = useState({});
  const [availableYears, setAvailableYears] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [monthCounts, setMonthCounts] = useState({});  // Event counts per month
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);

  // UI state
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [view, setView] = useState('month');
  const [expanded, setExpanded] = useState(initialExpanded);
  const effectiveExpanded = canToggleExpanded ? expanded : true;
  const isCollapsedWidget = isWidgetVariant && !effectiveExpanded;

  useEffect(() => {
    if (typeof initialExpanded === 'boolean') {
      setExpanded(initialExpanded);
    }
  }, [initialExpanded]);

  useEffect(() => {
    if (!canToggleExpanded) {
      setExpanded(true);
    }
  }, [canToggleExpanded]);

  // Request calendar data from server
  useEffect(() => {
    if (!effectiveExpanded) {
      setLoading(false);
      return;
    }

    if (!caseId) {
      setLoading(false);
      return;
    }

    if (!sendMessage || !subscribeToMessages) {
      console.error('CalendarOptimized: WebSocket not available');
      setLoading(false);
      return;
    }

    // Wait for an active socket before requesting data.
    // Without this, first render can fire too early and never retry.
    if (!isConnected || isValidatingSession) {
      setLoading(true);
      return;
    }

    setLoading(true);

    const handleCalendarData = (message) => {
      // Ensure we have the data object
      if (!message || !message.data) {
        console.error('Invalid message format:', message);
        setLoading(false);
        return;
      }

      const data = message.data;

      if (data.error) {
        console.error('Calendar data error:', data.error);
        setLoading(false);
        return;
      }

      setCalendarData(data.calendar_data || {});
      setAvailableYears(data.available_years || []);
      setAvailableDates(data.available_dates || []);
      setMonthCounts(data.month_counts || {});
      setTotalEvents(data.total_events || 0);
      setLoading(false);
    };

    const unsubscribe = subscribeToMessages(['getCalendarData', 'getcalendardata'], handleCalendarData);

    // Request calendar data for current visible month/year
    try {
      sendMessage({
        command: 'getCalendarData',
        caseid: caseId,
        maxEventsPerDay: maxEventsPerDay,
        year: view === 'year' ? currentYear : currentYear,
        month: view === 'month' ? currentMonth + 1 : undefined  // Send 1-12, not 0-11
      });
    } catch (error) {
      console.error('Failed to send calendar data request:', error);
      setLoading(false);
    }

    // Add timeout fallback in case response never comes
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 30000);

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, currentYear, currentMonth, view, effectiveExpanded, isConnected, isValidatingSession, maxEventsPerDay, sendMessage, subscribeToMessages]);

  // Compute available months for current year
  const availableMonths = useMemo(() => {
    return availableDates
      .filter(d => d.startsWith(`${currentYear}-`))
      .map(d => parseInt(d.split('-')[1]) - 1) // 0-indexed
      .sort((a, b) => a - b);
  }, [availableDates, currentYear]);

  // Ensure current month is valid
  useEffect(() => {
    if (availableMonths.length > 0 && !availableMonths.includes(currentMonth)) {
      setCurrentMonth(availableMonths[0]);
    }
  }, [availableMonths, currentMonth]);

  const toggleExpanded = () => {
    if (!canToggleExpanded) {
      return;
    }
    setExpanded(prev => {
      const next = !prev;
      if (onExpandedChange) {
        onExpandedChange(next);
      }
      return next;
    });
  };

  const handleEventClick = (event) => {
    if (navigateTo && typeof navigateTo === 'function') {
      navigateTo(event.id);
    }
  };

  const handlePrev = () => {
    if (view === 'month') {
      if (currentMonth === 0) {
        setCurrentYear(currentYear - 1);
        setCurrentMonth(11);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else if (view === 'year') {
      setCurrentYear(currentYear - 1);
    }
  };

  const handleNext = () => {
    if (view === 'month') {
      if (currentMonth === 11) {
        setCurrentYear(currentYear + 1);
        setCurrentMonth(0);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    } else if (view === 'year') {
      setCurrentYear(currentYear + 1);
    }
  };

  const enhancedTooltipProps = (event) => ({
    arrow: true,
    placement: "top",
    enterDelay: 300,
    leaveDelay: 200,
    componentsProps: {
      tooltip: {
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          p: 0,
          maxWidth: 'none',
          '& .MuiTooltip-arrow': {
            color: generateSaturatedColor(event.eventType) || primaryColor,
          }
        }
      },
      popper: {
        sx: {
          '& .MuiTooltip-tooltip': {
            filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.2))',
          }
        }
      }
    }
  });

  const renderEventDots = (dateKey, dayData) => {
    if (!dayData) {
      return null;
    }

    const { events, is_aggregated, total_count, hidden_count } = dayData;

    if (!events || events.length === 0) {
      return null;
    }

    return (
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        gap: { xs: 0.2, sm: 0.3 },
        mt: { xs: 0.3, sm: 0.5 },
        width: '100%',
        overflow: 'hidden',
        maxHeight: '100%'
      }}>
        {events.map((event, idx) => (
          <Tooltip
            key={`${event.id}-${idx}`}
            title={<TooltipContent event={{ ...event, date: dateKey }} />}
            {...enhancedTooltipProps(event)}
          >
            <Box
              onClick={() => handleEventClick(event)}
              sx={{
                ...commonStyles.eventBox,
                backgroundColor: generatePastelColor(event.eventType),
                borderColor: generateSaturatedColor(event.eventType),
              }}
            />
          </Tooltip>
        ))}

        {is_aggregated && (
          <Tooltip title={`${total_count} total events (showing ${events.length}, ${hidden_count} more)`}>
            <Chip
              label={`+${hidden_count}`}
              size="small"
              sx={{
                height: { xs: 16, sm: 18 },
                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                backgroundColor: primaryColor,
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold',
                minWidth: 'fit-content',
                '& .MuiChip-label': {
                  px: { xs: 0.5, sm: 0.75 }
                },
                '&:hover': {
                  backgroundColor: hoverColor,
                }
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderMonthView = () => {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const firstDayIndex = new Date(currentYear, currentMonth, 1).getDay();
    const numDays = new Date(currentYear, currentMonth + 1, 0).getDate();
    const paddedDaysArray = [
      ...Array(firstDayIndex).fill(null),
      ...Array.from({ length: numDays }, (_, index) => new Date(currentYear, currentMonth, index + 1))
    ];

    return (
      <Box sx={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: { xs: 0.5, sm: 1 } }}>
          {dayNames.map((day, i) => (
            <Typography
              key={i}
              variant="subtitle2"
              align="center"
              sx={{
                fontWeight: 'bold',
                color: primaryColor,
                fontSize: { xs: '0.65rem', sm: '0.875rem' }
              }}
            >
              {isMobile ? day.substring(0, 1) : day}
            </Typography>
          ))}
        </Box>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: { xs: 0.3, sm: 0.5 },
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {paddedDaysArray.map((day, index) => {
            if (!day) {
              return <Box
                key={`empty-${index}`}
                sx={{
                  minHeight: { xs: 70, sm: 100 }
                }}
              />;
            }

            const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
            const dayData = calendarData[dateKey];
            const hasEvents = dayData && dayData.total_count > 0;

            return (
              <Box
                key={index}
                sx={{
                  border: '1px solid',
                  borderColor: hasEvents ? primaryColor : '#e0e0e0',
                  borderRadius: { xs: 1, sm: 2 },
                  p: { xs: 0.5, sm: 1 },
                  minHeight: { xs: 70, sm: 100 },
                  maxHeight: { xs: 70, sm: 100 },
                  height: { xs: 70, sm: 100 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  backgroundColor: hasEvents ? lightPurple : 'white',
                  transition: 'all 0.2s ease',
                  overflow: 'hidden',
                  '&:hover': hasEvents ? {
                    backgroundColor: '#e8e8ff',
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 12px rgba(87, 87, 176, 0.2)'
                  } : {}
                }}
              >
                <Typography
                  variant="body2"
                  align="center"
                  fontWeight="bold"
                  color={primaryColor}
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, flexShrink: 0 }}
                >
                  {day.getDate()}
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                  {renderEventDots(dateKey, dayData)}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderYearView = () => {
    const monthsArray = Array.from({ length: 12 }, (_, index) => new Date(currentYear, index, 1));

    return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: { xs: 0.5, sm: 1 } }}>
        {monthsArray.map((month, index) => {
          const monthKey = `${currentYear}-${index + 1}`;
          const monthHasEvents = availableDates.some(d => d === monthKey);
          const eventCount = monthCounts[monthKey] || 0;

          return (
            <Box
              key={index}
              sx={{
                border: '1px solid',
                borderColor: monthHasEvents ? primaryColor : '#e0e0e0',
                borderRadius: { xs: 1, sm: 2 },
                p: { xs: 1, sm: 2 },
                textAlign: 'center',
                cursor: monthHasEvents ? 'pointer' : 'default',
                backgroundColor: monthHasEvents ? lightPurple : 'white',
                transition: 'all 0.2s ease',
                '&:hover': monthHasEvents ? {
                  backgroundColor: '#e8e8ff',
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 12px rgba(87, 87, 176, 0.2)'
                } : {}
              }}
              onClick={() => {
                if (monthHasEvents) {
                  setCurrentMonth(index);
                  setView('month');
                }
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color={primaryColor}
                sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
              >
                {t(`calendar.months.${index}`)}
              </Typography>
              {monthHasEvents && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, mt: 0.5 }}
                >
                  {eventCount} {eventCount === 1 ? 'event' : 'events'}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const Container = isWidgetVariant ? Box : Card;
  const ContentContainer = isWidgetVariant ? Box : CardContent;
  const containerSx = isWidgetVariant ? {} : {
    boxShadow: '0 12px 28px rgba(87, 87, 176, 0.15)',
    borderRadius: '16px',
    border: `1px solid ${lightPurple}`,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    marginTop: '10px !important',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    position: 'relative',
    '&:hover': {
      boxShadow: '0 16px 32px rgba(87, 87, 176, 0.2)',
      transform: loading ? 'none' : 'translateY(-5px)'
    },
  };
  const contentSx = isWidgetVariant
    ? {
      p: isCollapsedWidget ? 0 : { xs: 1, sm: 1.5 },
      backgroundColor: isCollapsedWidget ? 'transparent' : 'rgba(248, 250, 252, 0.4)',
      boxSizing: 'border-box'
    }
    : {
      padding: { xs: '12px', sm: '20px' },
      overflow: 'hidden',
      boxSizing: 'border-box'
    };

  return (
    <Container sx={containerSx}>
      <ContentContainer sx={contentSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: shouldStackHeader ? 'flex-start' : 'space-between',
            alignItems: shouldStackHeader ? 'stretch' : (isWidgetVariant ? 'flex-start' : 'center'),
            flexWrap: shouldStackHeader ? 'nowrap' : 'wrap',
            flexDirection: shouldStackHeader ? 'column' : 'row',
            gap: shouldStackHeader ? 0.5 : (isWidgetVariant ? 0.5 : { xs: 1, sm: 0 }),
            mb: isWidgetVariant
              ? (isCollapsedWidget ? 0 : (shouldStackHeader ? 0.75 : 1))
              : 2,
            pb: isWidgetVariant
              ? (isCollapsedWidget ? 0 : (shouldStackHeader ? 0.25 : 0.75))
              : 0,
            borderBottom: isWidgetVariant && !isCollapsedWidget
              ? '1px solid rgba(87, 87, 176, 0.12)'
              : 'none',
            position: shouldStackHeader ? 'relative' : 'static',
            pr: shouldStackHeader ? 5 : 0,
            ...(isCollapsedWidget ? {
              backgroundColor: '#ffffff',
              border: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 6px 10px rgba(87, 87, 176, 0.04)',
              borderRadius: '16px',
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.75, sm: 1 }
            } : {})
          }}
        >
          <Typography
            variant={isWidgetVariant ? 'h6' : 'h5'}
            fontWeight={isWidgetVariant ? 600 : 700}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: primaryColor,
              textShadow: '0px 1px 2px rgba(87, 87, 176, 0.15)',
              ...(isWidgetVariant ? { fontSize: { xs: '0.92rem', sm: '1.05rem' }, lineHeight: 1.4 } : {})
            }}
          >
            <EventIcon sx={{ mr: 1.5, verticalAlign: 'middle', color: primaryColor }} />
            {t('calendar.calendar')} {totalEvents > 0 && `(${totalEvents} events)`}
          </Typography>
          {canToggleExpanded && (
            <IconButton
              onClick={toggleExpanded}
              size={isWidgetVariant ? 'small' : 'medium'}
              sx={isWidgetVariant ? {
                backgroundColor: 'transparent',
                color: primaryColor,
                width: 32,
                height: 32,
                position: shouldStackHeader ? 'absolute' : 'static',
                top: shouldStackHeader ? 0 : 'auto',
                right: shouldStackHeader ? 0 : 'auto',
                zIndex: shouldStackHeader ? 1 : 'auto',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(87, 87, 176, 0.08)',
                  color: hoverColor,
                  transform: 'scale(1.1)'
                },
              } : {
                backgroundColor: 'transparent',
                color: primaryColor,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(87, 87, 176, 0.08)',
                  color: hoverColor,
                  transform: 'scale(1.1)'
                },
              }}
            >
              {effectiveExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        <Collapse in={effectiveExpanded} timeout='auto' unmountOnExit>
          <Box sx={{ mt: 0, width: '100%', overflow: 'hidden', boxSizing: 'border-box' }}>
            <ButtonGroup
              variant="outlined"
              orientation="horizontal"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 2,
                width: { xs: '100%', sm: 'auto' },
                mx: 'auto',
                '& .MuiButtonGroup-grouped': {
                  flex: { xs: 1, sm: '0 0 auto' },
                  minWidth: { xs: 0, sm: 120 }
                }
              }}
            >
              <Button
                onClick={() => setView('month')}
                variant={view === 'month' ? 'contained' : 'outlined'}
                disabled={loading}
                sx={{
                  backgroundColor: view === 'month' ? primaryColor : 'white',
                  color: view === 'month' ? '#fff' : primaryColor,
                  ...commonStyles.button
                }}
              >
                {t('calendar.month')}
              </Button>
              <Button
                onClick={() => setView('year')}
                variant={view === 'year' ? 'contained' : 'outlined'}
                disabled={loading}
                sx={{
                  backgroundColor: view === 'year' ? primaryColor : 'white',
                  color: view === 'year' ? '#fff' : primaryColor,
                  ...commonStyles.button
                }}
              >
                {t('calendar.year')}
              </Button>
            </ButtonGroup>

            {view === 'month' && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={primaryColor}
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {t(`calendar.months.${currentMonth}`)} {currentYear}
                </Typography>
              </Box>
            )}

            {view === 'year' && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  rowGap: 1,
                  flexWrap: 'wrap',
                  mb: 2
                }}
              >
                <IconButton onClick={handlePrev} disabled={loading} sx={{ backgroundColor: lightPurple, color: primaryColor }}>
                  <ArrowBackIcon />
                </IconButton>

                {availableYears.length > 0 && (
                  <FormControl size="small" sx={{ minWidth: { xs: 90, sm: 110 } }}>
                    <Select
                      value={currentYear}
                      onChange={(e) => setCurrentYear(e.target.value)}
                      disabled={loading}
                    >
                      {availableYears.map(year => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <IconButton onClick={handleNext} disabled={loading} sx={{ backgroundColor: lightPurple, color: primaryColor }}>
                  <ArrowForwardIcon />
                </IconButton>
              </Box>
            )}

            <Box sx={{ width: '100%', overflow: 'hidden', boxSizing: 'border-box', position: 'relative' }}>
              {view === 'month' && renderMonthView()}
              {view === 'year' && renderYearView()}

              {/* Loading Overlay - Only blurs the grid */}
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 10,
                    borderRadius: '8px'
                  }}
                >
                  <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress
                      size={60}
                      thickness={4}
                      sx={{
                        color: primaryColor,
                        mb: 2
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        color: primaryColor,
                        fontWeight: 600
                      }}
                    >
                      {t('calendar.loading', 'Loading calendar...')}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Collapse>
      </ContentContainer>
    </Container>
  );
};

export default CalendarOptimized;
